import isNil from 'lodash/isNil';
import { getWorkflow } from '@jira/project-settings__workflow-services-document/src/index.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { getApiPrefix } from '../common/utils.tsx';
import { transformWithUsages } from '../../utils/workflow-transformer/index.tsx';
import { transform, transformAPIToMetaDocument } from './transformer.tsx';
import { validateLogicalDocumentIntegrity } from './validator.tsx';
import type { ApiGetWorkflowResponseWithUsages } from './types.tsx';

// can be removed with clean up of 'hix-6586_support_wfe_no_nav_or_sidebar_route' as has moved to a shared package
export const constructEndpoint = (cloudId: string) => {
	return `/${getApiPrefix()}/query/${cloudId}/3/workflow`;
};

type WorkflowIdentifier = {
	workflowId?: string;
	workflowName?: string;
	projectAndIssueType?: {
		projectId: string;
		issueTypeId: string;
	};
};

type GetWorkflowPayload = {
	workflowIds?: [string];
	workflowNames?: [string];
	projectAndIssueTypes?: [
		{
			projectId: string;
			issueTypeId: string;
		},
	];
	skipUsageLookup?: boolean;
	checkForDrafts?: boolean;
};

export type GetWorkflowConfigOptions = { checkForDrafts: boolean };

// can be removed with clean up of 'hix-6586_support_wfe_no_nav_or_sidebar_route' as has moved to a shared package
const getWorkflowOld = (
	cloudId: string,
	workflowIdentifier: WorkflowIdentifier,
	config?: GetWorkflowConfigOptions,
) => {
	const url = constructEndpoint(cloudId);
	const { workflowId, workflowName, projectAndIssueType } = workflowIdentifier;

	const payload: GetWorkflowPayload = {
		skipUsageLookup: false,
	};

	if (!isNil(workflowId)) {
		payload.workflowIds = [workflowId];
	}

	if (!isNil(workflowName)) {
		payload.workflowNames = [workflowName];
	}

	if (!isNil(projectAndIssueType)) {
		payload.projectAndIssueTypes = [projectAndIssueType];
	}

	if (config?.checkForDrafts && fg('hix-6846-discard-wfl-drafts-on-update')) {
		payload.checkForDrafts = true;
	}

	return performPostRequest(url, {
		body: JSON.stringify(payload),
	}).then((result: ApiGetWorkflowResponseWithUsages) => {
		try {
			validateLogicalDocumentIntegrity(result);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			// an error here will not block the user experience, but it triggers
			// a priority alarm for us to investigate and understand the occurrence.
			log.safeErrorWithoutCustomerData('workflow.render.integrity.checker', e.message);
		}

		return fg('restore-from-workflow-history-versions-hix-4042')
			? transformWithUsages(result)
			: transform(result);
	});
};

const getWorkflowNew = async (
	cloudId: string,
	workflowIdentifier: WorkflowIdentifier,
	config?: GetWorkflowConfigOptions,
) => {
	const result = await getWorkflow(cloudId, workflowIdentifier, config);
	return transformAPIToMetaDocument(result);
};

export default functionWithCondition(
	() => fg('hix-6586_support_wfe_no_nav_or_sidebar_route'),
	getWorkflowNew,
	getWorkflowOld,
);
